import { useEffect, useRef, useState } from "react";
import { getErrorMessage } from "@/lib/utils";
import whyMeDefault from "@/assets/whyme-default.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Sparkles,
  Pencil,
  Quote,
  Award,
  Zap,
  Target,
  ImageIcon,
  X,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

interface WhyMeRow {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  image_url: string | null;
}

const WhyMe = () => {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();

  const [row, setRow] = useState<WhyMeRow | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    content: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchRow = async () => {
    const { data, error } = await supabase
      .from("why_me_content")
      .select("id, title, subtitle, content, image_url")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data) setRow(data as WhyMeRow);
  };

  useEffect(() => {
    fetchRow();
  }, []);

  const openEdit = () => {
    setForm({
      title: row?.title ?? t.whyme.title,
      subtitle: row?.subtitle ?? t.whyme.subtitle,
      content: row?.content ?? t.whyme.content,
    });
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(row?.image_url ?? null);
    setOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Gambar terlalu besar",
        description: "Maksimal 5MB",
        variant: "destructive",
      });
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return existingImageUrl;
    const ext = imageFile.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
      .from("whyme-images")
      .upload(fileName, imageFile);
    if (error) {
      toast({
        title: "Upload gagal",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }
    const { data } = supabase.storage.from("whyme-images").getPublicUrl(fileName);
    return data.publicUrl;
  };

  const deleteImageFromStorage = async (url: string | null) => {
    if (!url) return;
    const path = url.split("/whyme-images/")[1];
    if (path) {
      await supabase.storage.from("whyme-images").remove([path]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let imageUrl: string | null = existingImageUrl;
      if (imageFile) {
        if (row?.image_url && row.image_url !== existingImageUrl) {
          await deleteImageFromStorage(row.image_url);
        }
        imageUrl = await uploadImage();
        if (!imageUrl && imageFile) {
          setSaving(false);
          return;
        }
      } else if (row?.image_url && !existingImageUrl) {
        await deleteImageFromStorage(row.image_url);
        imageUrl = null;
      }

      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        content: form.content.trim(),
        image_url: imageUrl,
        updated_at: new Date().toISOString(),
      };

      if (row?.id) {
        const { error } = await supabase
          .from("why_me_content")
          .update(payload)
          .eq("id", row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("why_me_content")
          .insert(payload);
        if (error) throw error;
      }

      toast({ title: "Tersimpan", description: "Konten Why Me diperbarui." });
      setOpen(false);
      await fetchRow();
    } catch (e) {
      toast({
        title: "Gagal menyimpan",
        description: getErrorMessage(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const title = row?.title || t.whyme.title;
  const subtitle = row?.subtitle || t.whyme.subtitle;
  const content = row?.content || t.whyme.content;
  const imageUrl = row?.image_url;

  const stats = [
    { icon: Award, value: "5+", label: "Years" },
    { icon: Target, value: "20+", label: "Projects" },
    { icon: Zap, value: "8", label: "Languages" },
  ];

  return (
    <section
      id="whyme"
      className="py-20 sm:py-24 md:py-32 relative overflow-hidden"
    >
      {/* Decorative background blobs */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-primary/15 blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 -right-32 w-[28rem] h-[28rem] rounded-full bg-accent/15 blur-3xl animate-pulse"
          style={{ animationDelay: "1.5s" }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Eyebrow */}
        <div className="text-center mb-10 sm:mb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 mb-4 text-xs font-semibold tracking-[0.2em] uppercase text-primary">
            <span className="h-px w-8 bg-primary/50" />
            <Sparkles className="h-3.5 w-3.5" />
            <span>Why Choose Me</span>
            <Sparkles className="h-3.5 w-3.5" />
            <span className="h-px w-8 bg-primary/50" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight pb-1">
            {title}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Magazine split layout */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Visual Panel */}
          <div className="relative animate-slide-in-left scroll-reveal-left">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden glass-panel glass-sheen">
              <img
                src={imageUrl || whyMeDefault}
                alt={title}
                loading="lazy"
                width={832}
                height={1024}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              {/* Subtle gradient overlay for chip legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-background/30 pointer-events-none" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_80%,hsl(var(--accent)/0.25),transparent_55%)] pointer-events-none" />

              {/* Shimmer sweep */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-shimmer" />
              </div>
            </div>

            {/* Floating chips */}
            <div className="absolute -top-3 -right-3 sm:top-6 sm:-right-6 animate-float">
              <div className="glass-panel rounded-2xl pl-2.5 pr-3.5 py-2.5 flex items-center gap-3 shadow-2xl border border-primary/20 backdrop-blur-xl">
                {/* Medal seal */}
                <div className="relative h-10 w-10 shrink-0">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary via-accent to-primary shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.4),0_4px_12px_hsl(var(--primary)/0.45)]" />
                  <div className="absolute inset-[2px] rounded-full ring-1 ring-primary-foreground/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BadgeCheck className="h-5 w-5 text-primary-foreground drop-shadow" strokeWidth={2.5} />
                  </div>
                </div>
                {/* Divider */}
                <div className="h-9 w-px bg-gradient-to-b from-transparent via-border to-transparent" />
                {/* Text */}
                <div className="flex flex-col leading-none">
                  <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                    Dataiku Certified
                  </span>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent leading-none">
                      6
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.15em] text-foreground/70 font-semibold">
                      Credentials
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute -bottom-3 -left-3 sm:bottom-8 sm:-left-6 animate-float"
              style={{ animationDelay: "1s" }}
            >
              <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-2 shadow-xl">
                <div className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Status
                  </div>
                  <div className="text-xs font-bold">Open to Work</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Panel */}
          <div className="relative animate-slide-in-right scroll-reveal-right">
            <div className="glass-panel glass-sheen rounded-3xl p-7 sm:p-9 relative">
              {isAdmin && (
                <Button
                  size="icon"
                  variant="glass"
                  className="absolute top-4 right-4 h-9 w-9 rounded-full z-10"
                  onClick={openEdit}
                  aria-label="Edit Why Me"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}

              {/* Decorative quote mark */}
              <Quote
                className="absolute -top-2 -left-2 h-16 w-16 text-primary/15 rotate-180"
                strokeWidth={1.5}
              />

              <p className="relative text-base sm:text-lg leading-relaxed text-foreground/90 text-justify whitespace-pre-line font-light tracking-wide">
                {content}
              </p>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-6" />

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {stats.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={i}
                      className="glass-panel rounded-2xl p-3 sm:p-4 text-center hover:scale-105 transition-transform duration-300"
                    >
                      <Icon className="h-5 w-5 mx-auto mb-1.5 text-primary" />
                      <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                        {s.value}
                      </div>
                      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Why Me</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Image uploader */}
            <div>
              <Label>Cover Image</Label>
              <div className="mt-2 aspect-[4/5] rounded-xl border-2 border-dashed border-border bg-muted/30 overflow-hidden relative group">
                {imagePreview || existingImageUrl ? (
                  <>
                    <img
                      src={imagePreview || existingImageUrl || ""}
                      alt="preview"
                      className="h-full w-full object-cover"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      onClick={removeImage}
                      className="absolute top-2 right-2 h-7 w-7"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors">
                    <ImageIcon className="h-10 w-10 mb-2" />
                    <span className="text-xs">Click to upload</span>
                    <span className="text-[10px] mt-1">Max 5MB</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
              {(imagePreview || existingImageUrl) && (
                <label className="mt-2 inline-flex">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Replace image
                  </Button>
                </label>
              )}
            </div>

            {/* Fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="whyme-title">Title</Label>
                <Input
                  id="whyme-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whyme-subtitle">Subtitle</Label>
                <Input
                  id="whyme-subtitle"
                  value={form.subtitle}
                  onChange={(e) =>
                    setForm({ ...form, subtitle: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whyme-content">Content</Label>
                <Textarea
                  id="whyme-content"
                  rows={10}
                  value={form.content}
                  onChange={(e) =>
                    setForm({ ...form, content: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Batal
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default WhyMe;
